// Admin trip pipeline: types, labels and the filter model for the Trips screen.
// The stage itself is decided in the database (admin_trip_pipeline()); this file
// only presents and filters what comes back. Spec: docs/admin/trip-pipeline.md

export const STAGES = ['chat', 'payment', 'handbook', 'trip_started', 'trip_completed', 'survey_answered'] as const;
export type Stage = (typeof STAGES)[number] | 'exited' | 'abandoned';

export const STAGE_LABEL: Record<Stage, string> = {
  chat: 'Chat',
  payment: 'Payment',
  handbook: 'Preparing handbook',
  trip_started: 'Trip started',
  trip_completed: 'Trip completed',
  survey_answered: 'Survey answered',
  exited: 'Exited',
  abandoned: 'Abandoned',
};

export const STAGE_ICON: Record<Stage, string> = {
  chat: 'ti-message-circle',
  payment: 'ti-credit-card',
  handbook: 'ti-notebook',
  trip_started: 'ti-plane-departure',
  trip_completed: 'ti-flag-check',
  survey_answered: 'ti-star',
  exited: 'ti-door-exit',
  abandoned: 'ti-ghost',
};

export const SUB_LABEL: Record<string, string> = {
  chatting: 'Chatting with Alma',
  choosing_host: 'Choosing a host',
  no_hosts: 'Finished, no hosts',
  checkout_open: 'Checkout open',
  paid_waiting_host: 'Paid, waiting for host',
  none: 'No handbook yet',
  draft: 'Handbook draft',
  awaiting_approval: 'Sent for approval',
  approved: 'Handbook approved',
  travelling: 'Travelling',
  survey_pending: 'Survey not sent yet',
  survey_sent: 'Survey sent',
  answered: 'Answered',
  declined: 'Declined',
  refunded: 'Refunded',
  archived: 'Archived',
  superseded: 'Replaced by another match',
};

export const FLAG_LABEL: Record<string, string> = {
  host_silent: 'Host silent 24h+',
  handbook_late: 'Handbook late',
  ended_waiting_host: 'Ended, host never replied',
  survey_unanswered: 'Survey unanswered',
  no_dates: 'No dates',
  payment_failed: 'Payment failed',
  refund_problem: 'Refund problem',
  reported_problem: 'Traveler reported a problem',
  payout_held: 'Payout held 7d+',
};

export type Health = 'ok' | 'attention';

export interface Journey {
  journey_id: string;
  match_id: string | null;
  conversation_id: string | null;
  kind: 'trip' | 'lead';
  trip_name: string | null;
  city: string | null;
  traveler_id: string;
  traveler_name: string | null;
  host_id: string | null;
  host_name: string | null;
  created_at: string;
  stage: Stage;
  sub_state: string;
  entered_at: string;
  entered_approx: boolean;
  days_in_stage: number;
  start_date: string | null;
  end_date: string | null;
  match_status: string | null;
  payment_status: string | null;
  payout_status: string | null;
  itinerary_status: string | null;
  survey_status: string | null;
  flags: string[];
  milestones: Record<string, string>;
  archived: boolean;
}

export function health(j: Pick<Journey, 'flags'>): Health {
  return j.flags.length > 0 ? 'attention' : 'ok';
}

/** Index of the stage on the six-step rail; exited and abandoned sit off the rail. */
export function stageIndex(stage: Stage): number {
  return (STAGES as readonly string[]).indexOf(stage);
}

/** Rail milestones shown as done/current/todo for the six main stages. */
export function railState(j: Pick<Journey, 'stage'>, i: number): 'done' | 'current' | 'todo' {
  const at = stageIndex(j.stage);
  if (at < 0) return 'todo';
  if (i < at) return 'done';
  return i === at ? 'current' : 'todo';
}

// ── Filters ───────────────────────────────────────────────────────────────────

export interface TripFilters {
  stage: Stage | '';
  attention: boolean;
  host: string;
  city: string;
  dateField: 'created' | 'start';
  from: string;
  to: string;
  payment: string;
  handbook: string;
  survey: string;
  q: string;
  showAbandoned: boolean;
  showExited: boolean;
  showArchived: boolean;
  sort: 'stuck' | 'soonest' | 'newest';
}

export const EMPTY_FILTERS: TripFilters = {
  stage: '', attention: false, host: '', city: '', dateField: 'created', from: '', to: '',
  payment: '', handbook: '', survey: '', q: '',
  showAbandoned: false, showExited: false, showArchived: false, sort: 'stuck',
};

function day(iso: string | null): string {
  return iso ? iso.slice(0, 10) : '';
}

/** Everything except the stage, so strip counts can show "what each stage would hold". */
export function applyBase(all: Journey[], f: TripFilters): Journey[] {
  const q = f.q.trim().toLowerCase();
  return all.filter((j) => {
    if (j.archived && !f.showArchived) return false;
    if (j.stage === 'abandoned' && !f.showAbandoned && f.stage !== 'abandoned') return false;
    if (j.stage === 'exited' && !f.showExited && f.stage !== 'exited') return false;
    if (f.attention && j.flags.length === 0) return false;
    if (f.host && j.host_id !== f.host) return false;
    if (f.city && (j.city ?? '').toLowerCase() !== f.city.toLowerCase()) return false;
    if (f.payment && (j.payment_status ?? 'none') !== f.payment) return false;
    if (f.handbook && (j.itinerary_status ?? 'none') !== f.handbook) return false;
    if (f.survey && (j.survey_status ?? 'none') !== f.survey) return false;
    if (f.from || f.to) {
      const d = f.dateField === 'start' ? j.start_date ?? '' : day(j.created_at);
      if (!d) return false;
      if (f.from && d < f.from) return false;
      if (f.to && d > f.to) return false;
    }
    if (q) {
      const hay = `${j.trip_name ?? ''} ${j.traveler_name ?? ''} ${j.host_name ?? ''} ${j.city ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function applyFilters(all: Journey[], f: TripFilters): Journey[] {
  const rows = applyBase(all, f).filter((j) => !f.stage || j.stage === f.stage);
  return sortJourneys(rows, f.sort);
}

export function sortJourneys(rows: Journey[], sort: TripFilters['sort']): Journey[] {
  const out = [...rows];
  if (sort === 'stuck') out.sort((a, b) => b.days_in_stage - a.days_in_stage);
  else if (sort === 'newest') out.sort((a, b) => b.created_at.localeCompare(a.created_at));
  else out.sort((a, b) => (a.start_date ?? '9999').localeCompare(b.start_date ?? '9999'));
  return out;
}

/** Journeys per stage for the pipeline strip (ignores the stage filter itself). */
export function stageCounts(all: Journey[], f: TripFilters): Record<Stage, number> {
  const c = Object.fromEntries(([...STAGES, 'exited', 'abandoned'] as Stage[]).map((s) => [s, 0])) as Record<Stage, number>;
  for (const j of applyBase(all, f)) c[j.stage]++;
  return c;
}

export function attentionCount(rows: Journey[]): number {
  return rows.filter((j) => j.flags.length > 0).length;
}

// ── URL <-> filters ───────────────────────────────────────────────────────────

export function filtersToParams(f: TripFilters): URLSearchParams {
  const p = new URLSearchParams();
  const set = (k: string, v: string) => { if (v) p.set(k, v); };
  set('stage', f.stage);
  if (f.attention) p.set('attention', '1');
  set('host', f.host); set('city', f.city);
  if (f.from || f.to) { set('df', f.dateField); set('from', f.from); set('to', f.to); }
  set('pay', f.payment); set('hb', f.handbook); set('sv', f.survey); set('q', f.q);
  if (f.showAbandoned) p.set('ab', '1');
  if (f.showExited) p.set('ex', '1');
  if (f.showArchived) p.set('ar', '1');
  if (f.sort !== 'stuck') p.set('sort', f.sort);
  return p;
}

export function filtersFromParams(p: URLSearchParams): TripFilters {
  const stage = p.get('stage') ?? '';
  const valid = ([...STAGES, 'exited', 'abandoned'] as string[]).includes(stage);
  const sort = p.get('sort');
  return {
    stage: valid ? (stage as Stage) : '',
    attention: p.get('attention') === '1',
    host: p.get('host') ?? '',
    city: p.get('city') ?? '',
    dateField: p.get('df') === 'start' ? 'start' : 'created',
    from: p.get('from') ?? '',
    to: p.get('to') ?? '',
    payment: p.get('pay') ?? '',
    handbook: p.get('hb') ?? '',
    survey: p.get('sv') ?? '',
    q: p.get('q') ?? '',
    showAbandoned: p.get('ab') === '1',
    showExited: p.get('ex') === '1',
    showArchived: p.get('ar') === '1',
    sort: sort === 'soonest' || sort === 'newest' ? sort : 'stuck',
  };
}

// ── CSV ───────────────────────────────────────────────────────────────────────

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function journeysToCsv(rows: Journey[]): string {
  const head = ['Trip', 'Traveler', 'Host', 'City', 'Stage', 'Detail', 'Days in stage', 'Start', 'End', 'Payment', 'Payout', 'Handbook', 'Survey', 'Flags', 'Created'];
  const lines = rows.map((j) => [
    j.trip_name ?? '', j.traveler_name ?? '', j.host_name ?? '', j.city ?? '',
    STAGE_LABEL[j.stage], SUB_LABEL[j.sub_state] ?? j.sub_state, j.days_in_stage,
    j.start_date ?? '', j.end_date ?? '', j.payment_status ?? '', j.payout_status ?? '',
    j.itinerary_status ?? '', j.survey_status ?? '',
    j.flags.map((f) => FLAG_LABEL[f] ?? f).join('; '), day(j.created_at),
  ].map(csvCell).join(','));
  return [head.join(','), ...lines].join('\n');
}

// ── Milestones (detail panel) ─────────────────────────────────────────────────

export const MILESTONES: { key: string; label: string }[] = [
  { key: 'chat_started', label: 'Chat started' },
  { key: 'chat_finished', label: 'Chat finished' },
  { key: 'hosts_proposed', label: 'Hosts proposed' },
  { key: 'paid', label: 'Paid' },
  { key: 'host_notified', label: 'Host notified' },
  { key: 'host_confirmed', label: 'Host confirmed' },
  { key: 'handbook_started', label: 'Handbook started' },
  { key: 'handbook_submitted', label: 'Handbook sent for approval' },
  { key: 'handbook_approved', label: 'Handbook approved' },
  { key: 'trip_starts', label: 'Trip starts' },
  { key: 'trip_ends', label: 'Trip ends' },
  { key: 'marked_completed', label: 'Marked completed' },
  { key: 'payout_released', label: 'Payout released' },
  { key: 'survey_sent', label: 'Survey sent' },
  { key: 'survey_reminded', label: 'Survey reminder sent' },
  { key: 'survey_answered', label: 'Survey answered' },
];

export function timeline(j: Pick<Journey, 'milestones'>): { key: string; label: string; at: string }[] {
  return MILESTONES.filter((m) => j.milestones[m.key])
    .map((m) => ({ ...m, at: j.milestones[m.key] }))
    .sort((a, b) => a.at.localeCompare(b.at));
}
